import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useAresLookup, useAresSearch } from '../../api/hooks';
import type { AresDetail, AresSearchItem } from '../../api/schemas';
import styles from './AresSearchField.module.css';
import { cx } from '../../lib/cx';

export interface AresPickResult extends AresDetail {
  /** Celá adresa v jednom řetězci, tak jak ji chceme dávat do formulářů. */
  adresa: string;
}

export interface AresSearchFieldProps {
  /** Aktuální název firmy — controlled. */
  value: string;
  onChange: (value: string) => void;
  /** Callback po výběru firmy (z našeptávače nebo IČO lookupu). */
  onPick: (data: AresPickResult) => void;

  /** Volitelně: svázaná hodnota IČO; pokud je dodána, ukáže se IČO inline + tlačítko. */
  ico?: string;
  onIcoChange?: (value: string) => void;

  label?: React.ReactNode;
  placeholder?: string;
  /** Místo v-grid2 rámečku (NewCompanyModal) × tmavý „DocField“ styl (formuláře dokumentů). */
  appearance?: 'default' | 'doc';
  required?: boolean;
  hint?: React.ReactNode;
  /** Right-slot (např. DocContactQR tlačítko) — vykreslí se vpravo vedle textboxu. */
  rightSlot?: React.ReactNode;
  /** Pokud `false`, nevykreslí IČO řádek (hodí se pro rychlý název-only selector). */
  showIcoRow?: boolean;
}

function formatAdresa(d: AresDetail): string {
  const first = [d.ulice].filter(Boolean).join(' ');
  const second = [d.psc, d.mesto].filter(Boolean).join(' ');
  return [first, second].filter(Boolean).join(', ');
}

/**
 * Univerzální ARES vyhledávač. Kombinuje:
 *  – našeptávač podle názvu (min. 2 znaky, debounce)
 *  – lookup podle IČO (tlačítko „🔍 ARES“)
 * Port z frontend/index.html: aresSearchByName + aresLookupByIco + aresApplyData.
 */
export function AresSearchField({
  value,
  onChange,
  onPick,
  ico,
  onIcoChange,
  label,
  placeholder = 'Stavební firma s.r.o. (nebo IČO)',
  appearance = 'default',
  required,
  hint,
  rightSlot,
  showIcoRow = true,
}: AresSearchFieldProps) {
  const [debounced, setDebounced] = useState(value);
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState<
    | { kind: 'idle' }
    | { kind: 'loading'; msg: string }
    | { kind: 'ok'; msg: string }
    | { kind: 'error'; msg: string }
  >({ kind: 'idle' });
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const autoId = useId();

  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), 250);
    return () => clearTimeout(t);
  }, [value]);

  const searchQuery = useAresSearch(debounced);
  const [lookupIco, setLookupIco] = useState<string | null>(null);
  const lookupQuery = useAresLookup(lookupIco, { enabled: !!lookupIco });

  useEffect(() => {
    if (lookupQuery.isFetching) {
      setStatus({ kind: 'loading', msg: '⏳ Načítám z ARES…' });
    } else if (lookupQuery.isError) {
      setStatus({ kind: 'error', msg: '✗ Subjekt nenalezen. Zkontrolujte IČO.' });
    } else if (lookupQuery.isSuccess && lookupQuery.data) {
      const d = lookupQuery.data;
      const result: AresPickResult = { ...d, adresa: formatAdresa(d) };
      onPick(result);
      onChange(d.nazev);
      if (onIcoChange) onIcoChange(d.ico);
      setStatus({ kind: 'ok', msg: `✓ Načteno: ${d.nazev}` });
      setLookupIco(null);
    }
  }, [lookupQuery.isFetching, lookupQuery.isError, lookupQuery.isSuccess, lookupQuery.data, onChange, onPick, onIcoChange]);

  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const suggestions = useMemo<AresSearchItem[]>(() => {
    if (!open) return [];
    if (searchQuery.isFetching) return [];
    return searchQuery.data ?? [];
  }, [open, searchQuery.isFetching, searchQuery.data]);

  async function pickSuggestion(item: AresSearchItem) {
    setOpen(false);
    setStatus({ kind: 'loading', msg: '⏳ Načítám detail z ARES…' });
    setLookupIco(item.ico);
  }

  function handleIcoLookup() {
    const cleaned = (ico ?? '').replace(/\s+/g, '');
    if (!/^\d{6,8}$/.test(cleaned)) {
      setStatus({ kind: 'error', msg: '⚠ Zadejte platné IČO (6–8 číslic)' });
      return;
    }
    setStatus({ kind: 'loading', msg: '⏳ Načítám z ARES…' });
    setLookupIco(cleaned);
  }

  const doc = appearance === 'doc';
  const inputId = `ares-${autoId}`;

  return (
    <div className={cx(styles.field, doc && styles.fieldDoc)} ref={wrapRef}>
      {label && (
        <label htmlFor={inputId} className={cx(styles.label, doc && styles.labelDoc)}>
          {label}
          {required && <span className={styles.required}>*</span>}
        </label>
      )}
      <div className={cx(styles.nameRow, doc && styles.nameRowDoc)}>
        <input
          id={inputId}
          type="text"
          className={cx(styles.input, doc && styles.inputDoc)}
          value={value}
          onChange={(e) => {
            onChange(e.target.value);
            setOpen(true);
          }}
          onFocus={() => value.length >= 2 && setOpen(true)}
          placeholder={placeholder}
          autoComplete="off"
        />
        {rightSlot}
      </div>

      {open && value.trim().length >= 2 && (
        <div className={styles.suggestList} role="listbox">
          {searchQuery.isFetching && <div className={styles.suggestMsg}>Hledám…</div>}
          {!searchQuery.isFetching && suggestions.length === 0 && (searchQuery.isError ? (
            <div className={cx(styles.suggestMsg, styles.error)}>⚠ Chyba připojení k ARES.</div>
          ) : (
            <div className={styles.suggestMsg}>Žádné výsledky pro „{value.trim()}".</div>
          ))}
          {suggestions.map((s) => (
            <button
              key={s.ico}
              type="button"
              className={styles.suggestItem}
              onClick={() => pickSuggestion(s)}
            >
              <div className={styles.suggestName}>{s.nazev}</div>
              <div className={styles.suggestDetail}>
                IČO: <span className={styles.mono}>{s.ico}</span>  ·  {s.adresa}
              </div>
            </button>
          ))}
        </div>
      )}

      {showIcoRow && onIcoChange && (
        <div className={cx(styles.icoRow, doc && styles.icoRowDoc)}>
          <input
            type="text"
            className={cx(styles.input, styles.mono, styles.icoInput, doc && styles.inputDoc)}
            placeholder="IČO"
            value={ico ?? ''}
            onChange={(e) => onIcoChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleIcoLookup();
              }
            }}
          />
          <button
            type="button"
            className={styles.icoBtn}
            onClick={handleIcoLookup}
            disabled={lookupQuery.isFetching}
          >
            {lookupQuery.isFetching ? 'Načítám…' : '🔍 ARES'}
          </button>
        </div>
      )}

      {status.kind !== 'idle' ? (
        <div className={cx(styles.status, styles[status.kind])}>{status.msg}</div>
      ) : hint ? (
        <div className={styles.hint}>{hint}</div>
      ) : null}
    </div>
  );
}
